import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CategoryModal, {
  CategoryFormData,
} from "./category-modal/CategoryModal";
import "./ManageCategory.css";
import ReusableTable, {
  Column,
} from "../../../components/reusable-table/ReusableTable";
import Button from "../../../components/reusable-button/Button";
import { BiEditAlt } from "react-icons/bi";
import {
  getAllCategories,
  getAllCategoryTypes,
  uploadFile,
  createCategory,
  updateCourse,
} from "../../../apis/categoryApis";
import { showSnackbar } from "../../../redux/slices/snackbarSlice";
import { RootState } from "../../../redux/store/store";
import {
  setCategories,
  setNeedRefresh as setCategoryNeedRefresh,
} from "../../../redux/slices/categorySlice";
import {
  setCategoryTypes,
  setCategoryTypeNeedRefresh,
} from "../../../redux/slices/categoryTypeSlice";
import { getFileUrl } from "../../../utils/getFileUrl";
import ReusableLoader from "../../../components/reusable-loader/ReusableLoader";
import { TOAST_MESSAGES } from "../../../constants/toastMessages";

interface Category {
  id: string;
  title: string;
  subtitle: string;
  logo?: string;
  description: string;
  status: "Active" | "Inactive";
  categoryTypeId: string;
  fileId: string;
}

const EditIcon = BiEditAlt as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;

const ManageCategory: React.FC = () => {
  const dispatch = useDispatch();
  const { categories, needRefresh: categoryNeedRefresh } = useSelector(
    (state: RootState) => state.category
  );
  const { categoryTypes, needRefresh: categoryTypeNeedRefresh } = useSelector(
    (state: RootState) => state.categoryType
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [modalInitialData, setModalInitialData] = useState<
    CategoryFormData | undefined
  >();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null
  );
  const [loadingData, setLoadingData] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchCategoriesAndTypes = async () => {
    setLoadingData(true);
    try {
      const [res, categoryTypesRes] = await Promise.all([
        getAllCategories(),
        getAllCategoryTypes(),
      ]);

      const transformed = await Promise.all(
        res.data.map(async (item: any): Promise<Category> => {
          const subtitle = categoryTypesRes.data.find(
            (ct: any) => ct.id === item.categoryTypeId
          );

          let logoUrl: string | undefined;
          // if (item.files?.uuid) {
          logoUrl = await getFileUrl(item.files.uuid);
          // }

          return {
            id: item.id,
            title: item.courseName,
            subtitle: subtitle?.categoriesTypeName || "",
            logo: logoUrl,
            description: item.courseDescription,
            status: item.isActive ? "Active" : "Inactive",
            categoryTypeId: item.categoryTypeId,
            fileId: item.files.uuid,
          };
        })
      );

      dispatch(setCategories(transformed));
      dispatch(setCategoryTypes(categoryTypesRes.data));
    } catch (err) {
      console.error("Error loading categories or category types:", err);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (categoryNeedRefresh || categoryTypeNeedRefresh) {
      fetchCategoriesAndTypes();
    }
  }, [categoryNeedRefresh, categoryTypeNeedRefresh]);

  const handleSaveCategory = async (data: CategoryFormData) => {
    setLoading(true);
    try {
      // let fileId = "8f16a87a-907c-4c83-b965-690b6e6bb7a8";
      let fileId = "";
      if (data.logo) {
        const uploadRes = await uploadFile(data.logo, "logo");
        fileId = uploadRes?.data?.uuid || "";
      }

      const commonPayload = {
        courseName: data.name,
        courseDescription: data.description,
        fileId: (fileId || data.fileId) ?? "",
        categoryTypeId: data.type,
      };

      if (modalMode === "add") {
        const res = await createCategory(commonPayload);
        if (res?.success) {
          dispatch(
            showSnackbar({
              message: TOAST_MESSAGES.CATEGORY.CREATE_SUCCESS,
              type: "success",
            })
          );
          dispatch(setCategoryNeedRefresh(true));
          dispatch(setCategoryTypeNeedRefresh(true));
          setIsModalOpen(false);
        }
      } else if (selectedCategoryId) {
        const res = await updateCourse(selectedCategoryId, {
          ...commonPayload,
          isActive: data.active,
        });
        if (res?.success) {
          dispatch(
            showSnackbar({
              message: TOAST_MESSAGES.CATEGORY.UPDATE_SUCCESS,
              type: "success",
            })
          );
          dispatch(setCategoryNeedRefresh(true));
          dispatch(setCategoryTypeNeedRefresh(true));
          setIsModalOpen(false);
        }
      }
    } catch (err) {
      console.error("Error saving category:", err);
      dispatch(
        showSnackbar({
          message: TOAST_MESSAGES.CATEGORY.CATEGORY_ERROR,
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const adminDetail = localStorage.getItem("adminDetail");
  const roleName = adminDetail
    ? JSON.parse(adminDetail)?.roleIds?.roleName
    : null;

  const baseColumns: Column<Category>[] = [
    {
      header: "Category",
      render: (cat) => (
        <div className="category-info">
          <img src={cat.logo} alt={cat.title} className="category-logo" />
          <div>
            <div className="category-title">{cat.title}</div>
            <div className="category-subtitle">{cat.subtitle}</div>
          </div>
        </div>
      ),
    },
    { header: "Description", accessor: "description" },
    {
      header: "Status",
      render: (cat) => (
        <span
          className={`status ${
            cat.status === "Active" ? "active" : "inactive"
          }`}
        >
          {cat.status}
        </span>
      ),
    },
  ];

  const actionColumn: Column<Category> = {
    header: "",
    render: (cat) => (
      <button
        className="edit-button"
        onClick={() => {
          setModalMode("edit");
          setModalInitialData({
            logoUrl: cat.logo,
            type: cat.categoryTypeId,
            name: cat.title,
            description: cat.description,
            active: cat.status === "Active",
            fileId: cat.fileId,
          });
          setSelectedCategoryId(cat.id);
          setIsModalOpen(true);
        }}
      >
        <EditIcon style={{ fontSize: 16, color: "#28606B" }} />
      </button>
    ),
  };

  const columns =
    roleName === "admin" ? baseColumns : [...baseColumns, actionColumn];

  const categoryTypeOptions = categoryTypes.map((ct: any) => ({
    label: ct.categoriesTypeName,
    value: ct.id,
  }));

  return (
    <div className="manage-category">
      <div className="header">
        <h2>Manage Category ({categories.length})</h2>
        {roleName !== "admin" && (
          <div>
            <Button
              label="+ Add Category"
              onClick={() => {
                setModalMode("add");
                setModalInitialData(undefined);
                setSelectedCategoryId(null);
                setIsModalOpen(true);
              }}
            />
          </div>
        )}
      </div>

      {loadingData ? (
        <div className="loading">
          <ReusableLoader />
        </div>
      ) : (
        <ReusableTable columns={columns} rows={categories} />
      )}

      <CategoryModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveCategory}
        mode={modalMode}
        initialData={modalInitialData}
        loading={loading}
        categoryTypeOptions={categoryTypeOptions}
      />
    </div>
  );
};

export default ManageCategory;
